import type { ReactNode } from 'react';
import { Box, InlineStack, Paragraph, Spinner } from '@customerio/pluma-components/react';
import { useDsSmsBody } from '@/services/db/preview/queries';
import { sanitizeHtml, sanitizeRelaxedHtml } from '@/utils/sanitize';
import { isEmptyString } from '@/utils/string';
import {
  isDsSmsBody,
  isWhatsappContentTemplateBody,
  parseContentTemplateData,
  toContentTemplateView,
} from '../utils/twilio-content-template';
import { SmsContentTemplate } from './sms-content-template';
import * as styles from './delivery-message-preview.css';

type Alignment = 'center' | 'flex-start' | 'flex-end';

type SmsBubbleProps = { alignInline: Alignment; children: ReactNode };

function SmsBubble({ alignInline, children }: SmsBubbleProps) {
  return (
    <InlineStack alignInline={alignInline}>
      <div className={`${styles.smsBubble} fs-mask`} data-test-delivery-preview-sms>
        {children}
      </div>
    </InlineStack>
  );
}

type SmsTextProps = { body: string; isEndUserText?: boolean };

function SmsText({ body, isEndUserText = false }: SmsTextProps) {
  const sanitize = isEndUserText ? sanitizeHtml : sanitizeRelaxedHtml;
  const content = isEmptyString(body) ? '…' : sanitize(body);

  return (
    // nosemgrep: react-dangerouslysetinnerhtml — content is sanitized via DOMPurify in sanitizeHtml() / sanitizeRelaxedHtml()
    <div className={styles.smsBody} dangerouslySetInnerHTML={{ __html: content }} />
  );
}

type SmsMessagePreviewProps = {
  body: string;
  /** Where the bubble sits in its row. The delivery preview centers it; a message thread aligns it to the sender's side. */
  alignInline?: Alignment;
  /**
   * The text was written by an end user (an inbound reply), not sent by the workspace. It gets the
   * strict sanitizer, so a message can't make the viewer's browser load a remote image.
   */
  isEndUserText?: boolean;
};

/** A plain SMS bubble: the message text, sanitized. */
export function SmsMessagePreview({ body, alignInline = 'center', isEndUserText = false }: SmsMessagePreviewProps) {
  return (
    <SmsBubble alignInline={alignInline}>
      <SmsText body={body} isEndUserText={isEndUserText} />
    </SmsBubble>
  );
}

type SmsDeliveryPreviewProps = {
  body: string;
  /** The body may be a WhatsApp content template. It is drawn as one only when it carries a `contentTemplateId`. */
  isWhatsapp: boolean;
};

/**
 * An SMS body, from a delivery or a template. A WhatsApp body that carries a
 * `contentTemplateId` is a content template, drawn as a card. A Design Studio
 * body (`<x-sms>`) is rendered by the server. Anything else is a
 * plain bubble.
 */
export function SmsDeliveryPreview({ body, isWhatsapp }: SmsDeliveryPreviewProps) {
  const isDs = isDsSmsBody(body);
  const rendered = useDsSmsBody(body, isDs);

  if (isDs && rendered.isPending) {
    return (
      <InlineStack alignInline="center">
        <Box py="300" data-test-sms-preview-from-template-spinner>
          <Spinner color="secondary" />
        </Box>
      </InlineStack>
    );
  }
  if (isDs && rendered.isError) {
    return (
      <InlineStack alignInline="center">
        <Box py="300">
          <Paragraph color="secondary" data-test-sms-preview-from-template-error>
            Preview unavailable
          </Paragraph>
        </Box>
      </InlineStack>
    );
  }
  if (isDs) {
    return <SmsMessagePreview body={rendered.data ?? ''} />;
  }

  const template = isWhatsapp && isWhatsappContentTemplateBody(body) ? parseContentTemplateData(body) : undefined;
  if (template == null) {
    return <SmsMessagePreview body={body} />;
  }
  return (
    <SmsBubble alignInline="center">
      <SmsContentTemplate view={toContentTemplateView(template, body)} />
    </SmsBubble>
  );
}
